import React, { useState, useEffect } from "react";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { BsChevronDown, BsChevronRight, BsPlusLg, BsEye, BsEyeSlash, BsTrash } from "react-icons/bs";
import { HiPencil, HiCheck, HiX } from "react-icons/hi";
import { capitalizeWords } from "Utils/utils";
import PopoverContainer from "components/common/Popover/PopoverContainer";
import DocumentRow, { PARTICIPANT_DOC_GRID_CLASS } from "./DocumentRow";

const DROPPABLE_PREFIX = "category-";

// TODO(STAFF_COMPLIANCE_DELETE): FE-5/A10 – Pass onDelete and deleteLoading to DocumentRow (documents only).
const SortableDocumentRow = ({
  item,
  categoryName,
  complianceType,
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  onDelete,
  onEdit,
  deleteLoadingId,
  disabled: categoryDisabled,
  uploadSubject,
  showDateRequirements = false,
  onDateRequirementChange,
  mandatorySubtitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { item, fromCategory: categoryName },
    disabled: categoryDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragHandleProps = categoryDisabled
    ? {}
    : { attributes, listeners };

  return (
    <div ref={setNodeRef} style={style}>
      <DocumentRow
        item={item}
        complianceType={complianceType}
        lastErrorLocation={lastErrorLocation}
        onSwitchChange={onSwitchChange}
        onScoreChange={onScoreChange}
        onLastErrorChange={onLastErrorChange}
        dragHandleProps={dragHandleProps}
        onDelete={onDelete}
        onEdit={onEdit}
        deleteLoading={deleteLoadingId === item.uuid}
        disabled={categoryDisabled}
        uploadSubject={uploadSubject}
        showDateRequirements={showDateRequirements}
        onDateRequirementChange={onDateRequirementChange}
        mandatorySubtitle={mandatorySubtitle}
      />
    </div>
  );
};

export const getCategoryDroppableId = (name) => `${DROPPABLE_PREFIX}${name}`;

const DocumentCategoryCard = ({
  categoryName,
  items,
  complianceType,
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  onAddDocument,
  onDeleteDocument,
  onEditDocument,
  deleteDocumentLoadingId,
  activeDragId,
  activeDragFromCategory,
  overId,
  categoryId,
  isVisible,
  hasMandatoryDoc,
  onVisibilityToggle,
  onCategoryUpdate,
  onCategoryDelete,
  categoryUpdateLoading = false,
  categoryDeleteLoading = false,
  isSystemDefault = false,
  visibilityLoading = false,
  uploadSubject = "workers",
  showDateRequirements = false,
  onDateRequirementChange,
  mandatorySubtitle,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(categoryName);
  const [isExpanded, setIsExpanded] = useState(true);
  const isSavingRef = React.useRef(false);

  const isHidden = isVisible === false;

  useEffect(() => {
    setEditName(categoryName);
  }, [categoryName]);

  useEffect(() => {
    if (isSavingRef.current && !categoryUpdateLoading) {
      isSavingRef.current = false;
      setIsEditing(false);
    }
  }, [categoryUpdateLoading]);

  const { setNodeRef, isOver } = useDroppable({
    id: getCategoryDroppableId(categoryName),
    disabled: isHidden,
  });

  const handleAccept = () => {
    if (editName.trim() && editName !== categoryName && categoryId != null && onCategoryUpdate) {
      isSavingRef.current = true;
      onCategoryUpdate(categoryId, editName.trim());
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditName(categoryName);
    setIsEditing(false);
  };

  const displayName =
    categoryName !== "undefined"
      ? capitalizeWords(categoryName)
      : "Uncategorised";

  const isWithinCategoryReorder =
    activeDragId &&
    overId &&
    activeDragFromCategory === categoryName &&
    items.some((i) => i.id === overId);
  const isCrossCategoryDrop = isOver && !isWithinCategoryReorder;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-2 transition-all duration-200 overflow-hidden shadow-sm relative ${
        isCrossCategoryDrop
          ? "border-indigo-500 bg-indigo-50/50 shadow-md ring-2 ring-indigo-200 ring-offset-2"
          : isHidden
          ? "bg-gray-100 border-gray-300 opacity-75"
          : "bg-white border-gray-200"
      }`}
    >
      {isCrossCategoryDrop && (
        <div className="absolute inset-0 flex items-center justify-center bg-indigo-50/80 z-10 rounded-lg pointer-events-none">
          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-white border-2 border-indigo-400 rounded-lg shadow-sm">
            Drop here
          </span>
        </div>
      )}
      <div
        className={`flex items-center gap-2 px-4 py-3 border-b cursor-pointer select-none ${
          isHidden
            ? "bg-gray-200/80 border-gray-300 text-gray-500"
            : "bg-gray-50/80 border-gray-200"
        }`}
        onClick={() => !isEditing && setIsExpanded((prev) => !prev)}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditing) setIsExpanded((prev) => !prev);
          }}
          className={`flex-shrink-0 p-0.5 rounded ${isHidden ? "text-gray-400" : "text-gray-500 hover:text-indigo-600"}`}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <BsChevronDown className="w-4 h-4" />
          ) : (
            <BsChevronRight className="w-4 h-4" />
          )}
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0" onClick={(e) => isEditing && e.stopPropagation()}>
          {isHidden && (
            <span className="flex-shrink-0 text-xs font-medium text-gray-400 bg-gray-300/70 px-2 py-0.5 rounded">
              Hidden
            </span>
          )}
          {isEditing ? (
            <>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAccept();
                  if (e.key === "Escape") handleCancel();
                }}
                className="flex-1 px-2 py-0.5 text-sm font-medium border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleAccept}
                disabled={categoryUpdateLoading || !editName.trim() || editName.trim() === categoryName}
                className="flex-shrink-0 flex items-center gap-1.5 p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Save category name"
              >
                {categoryUpdateLoading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" aria-hidden />
                    <span className="text-xs font-medium text-green-700">Saving…</span>
                  </>
                ) : (
                  <HiCheck className="w-4 h-4" />
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={categoryUpdateLoading}
                className="flex-shrink-0 p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Cancel"
              >
                <HiX className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <span
                className={`text-sm font-medium truncate ${isHidden ? "text-gray-500" : "text-gray-900"} ${categoryId != null && onCategoryUpdate ? "cursor-pointer" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (categoryId != null && onCategoryUpdate) setIsEditing(true);
                }}
              >
                {displayName}
              </span>
              {categoryId != null && onCategoryUpdate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className={`flex-shrink-0 p-0.5 rounded ${isHidden ? "text-gray-400" : "text-gray-400 hover:text-indigo-600"}`}
                  aria-label="Edit category name"
                >
                  <HiPencil className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
        {categoryId != null && onVisibilityToggle && (
          <PopoverContainer
            cssClass="text-white text-sm bg-gradient p-2 rounded-md z-10"
            toolTip={
              items.length === 0
                ? "Add at least one document to enable visibility"
                : isVisible && hasMandatoryDoc
                ? "Uncheck Mandatory on all documents first to hide this category"
                : isVisible
                ? "Click here to hide this category"
                : "Click here to show this category"
            }
            placement="top"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const isEmpty = items.length === 0;
                if (visibilityLoading || isEmpty || (isVisible && hasMandatoryDoc)) return;
                onVisibilityToggle(categoryId, !isVisible);
              }}
              disabled={visibilityLoading || items.length === 0 || (isVisible && hasMandatoryDoc)}
              className="flex-shrink-0 p-1.5 rounded text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center justify-center min-w-[1.5rem]"
              aria-label={
                items.length === 0
                  ? "Add at least one document to enable visibility"
                  : isVisible
                  ? "Click here to hide this category"
                  : "Click here to show this category"
              }
            >
              {visibilityLoading ? (
                <span className="inline-block w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" aria-hidden />
              ) : isVisible ? (
                <BsEyeSlash className="w-4 h-4" />
              ) : (
                <BsEye className="w-4 h-4" />
              )}
            </button>
          </PopoverContainer>
        )}
        {categoryId != null && onCategoryDelete && !isSystemDefault && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasMandatoryDoc || categoryDeleteLoading || isHidden) return;
              onCategoryDelete(categoryId, displayName);
            }}
            disabled={hasMandatoryDoc || categoryDeleteLoading || isHidden}
            title={
              isHidden
                ? "Show category first to delete it"
                : hasMandatoryDoc
                ? "Cannot delete: this category has mandatory documents. Uncheck Mandatory on all documents first."
                : "Delete category"
            }
            className="flex-shrink-0 p-1.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center justify-center min-w-[1.5rem]"
            aria-label="Delete category"
          >
            {categoryDeleteLoading ? (
              <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" aria-hidden />
            ) : (
              <BsTrash className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
      {isExpanded && (
        <>
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
            id={getCategoryDroppableId(categoryName)}
          >
            <div className={`divide-y min-h-[40px] relative ${isHidden ? "divide-gray-200 bg-gray-50" : "divide-gray-100 bg-white"}`}>
              {showDateRequirements && items.length > 0 && (
                <div
                  className={`hidden lg:grid ${PARTICIPANT_DOC_GRID_CLASS} gap-x-4 items-center px-4 py-2 bg-gray-50/90 border-b border-gray-200 text-xs font-medium text-gray-500`}
                >
                  <div aria-hidden="true" />
                  <div>Document</div>
                  <div className="text-center">Require issue date</div>
                  <div className="text-center">Require expiry date</div>
                  <div className="text-center">Mandatory</div>
                  <div className="text-center">Percentage</div>
                  <div aria-hidden="true" />
                </div>
              )}
              {items.length === 0 && (
                <p className="px-4 py-4 text-sm text-gray-500 text-center">
                  Please add a document or drag from another category.
                </p>
              )}
              {items.map((item) => (
                <SortableDocumentRow
                  key={item.id}
                  item={item}
                  categoryName={categoryName}
                  complianceType={complianceType}
                  lastErrorLocation={lastErrorLocation}
                  onSwitchChange={onSwitchChange}
                  onScoreChange={onScoreChange}
                  onLastErrorChange={onLastErrorChange}
                  onDelete={onDeleteDocument}
                  onEdit={onEditDocument}
                  deleteLoadingId={deleteDocumentLoadingId}
                  disabled={isHidden}
                  uploadSubject={uploadSubject}
                  showDateRequirements={showDateRequirements}
                  onDateRequirementChange={onDateRequirementChange}
                  mandatorySubtitle={mandatorySubtitle}
                />
              ))}
            </div>
          </SortableContext>
          {onAddDocument && (
            <div className={`flex justify-center py-3 px-4 border-t ${isHidden ? "bg-gray-100/80 border-gray-200" : "bg-gray-50/50 border-gray-100"}`}>
              <button
                type="button"
                disabled={isHidden}
                onClick={() => !isHidden && onAddDocument(categoryName)}
                title={isHidden ? "Show category to add documents" : "Add document"}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isHidden
                    ? "text-gray-500 bg-gray-200 border border-gray-300 cursor-not-allowed opacity-60"
                    : "text-indigo-600 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                }`}
              >
                <BsPlusLg className="w-4 h-4" />
                Add Document
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentCategoryCard;
export { DROPPABLE_PREFIX };
